import React, { type ReactNode } from "react";
import { ThemeProvider } from "styled-components";
import GlobalStyles from "./globals";

const breakpoints = {
  xs: "320px",
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
};

export const devices = {
  xs: `(min-width: ${breakpoints.xs})`,
  sm: `(min-width: ${breakpoints.sm})`,
  md: `(min-width: ${breakpoints.md})`,
  lg: `(min-width: ${breakpoints.lg})`,
  xl: `(min-width: ${breakpoints.xl})`,
  "2xl": `(min-width: ${breakpoints["2xl"]})`,
};
export const theme = {
  colors: {
    white: "#ffffff",
    black: "#000000",
    gray: "#D7D7D7",
    background: "#F7F8FC",

    text: "#252525",
    textGray: "#303030",
    blackBtn: "#343A40",

    primary: "#F3953F",
    secondary: "#B9BD5C",
    tertiary: "#EC6726",

    success: "#198754",
    error: "#DC3545",
    warning: "#FFC107",
    info: "#0DCAF0",

    // Semantic surface aliases
    surface: "#FFFFFF",
    surfaceMuted: "#F7F8FC",
    borderSubtle: "#E5E7EB",

    // Tints (use as backgrounds)
    primaryTintWeak: "rgba(243, 149, 63, 0.06)",
    primaryTintMedium: "rgba(243, 149, 63, 0.10)",
    primaryTintStrong: "rgba(243, 149, 63, 0.14)",
    warningSubtle: "#fffbeb",
    warningTint: "rgba(255, 193, 7, 0.12)",
    warningBorder: "rgba(255, 193, 7, 0.4)",
    errorTint: "rgba(220, 53, 69, 0.08)",
    errorBorder: "rgba(220, 53, 69, 0.3)",
    successTint: "rgba(25, 135, 84, 0.10)",

    // Inks (text on tints)
    warningInk: "#856404",
    errorInk: "#842029",
    successInk: "#0F5132",

    // Focus
    focusRing: "rgba(243, 149, 63, 0.35)",

    // Additional semantic
    card: "#FFFFFF",
    primaryDark: "#d15d20",
  },
  borderRadius: {
    xs: "2px",
    sm: "4px",
    md: "6px",
    lg: "8px",
    xl: "12px",
    "2xl": "16px",
    "3xl": "24px",
    full: "9999px",
  },
  shadows: {
    sm: "0 1px 3px rgba(0,0,0,0.08)",
    md: "0 2px 8px rgba(0,0,0,0.10)",
    lg: "0 4px 16px rgba(0,0,0,0.14)",
    card: "1px 1px 4px 1px rgba(0,0,0,0.10)",
    modal: "0 8px 32px rgba(0,0,0,0.18)",
  },
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "20px",
    "2xl": "24px",
    "3xl": "32px",
    "4xl": "40px",
    "5xl": "48px",
    "6xl": "64px",
  },
  zIndex: {
    dropdown: 100,
    sticky: 200,
    overlay: 300,
    modal: 400,
    toast: 500,
    tooltip: 600,
  },
  fontSize: {
    xs: "0.75rem" /* 12px */,
    sm: "0.875rem" /* 14px */,
    base: "0.9375rem" /* 15px */,
    md: "1rem" /* 16px */,
    lg: "1.125rem" /* 18px */,
    xl: "1.25rem" /* 20px */,
    "2xl": "1.5rem" /* 24px */,
    "3xl": "1.75rem" /* 28px */,
    "4xl": "2rem" /* 32px */,
    "5xl": "2.5rem" /* 40px */,
    "6xl": "3rem" /* 48px */,
    "7xl": "3.75rem" /* 60px */,
    "8xl": "4.5rem" /* 72px */,
    "9xl": "6rem" /* 96px */,
    "10xl": "8rem" /* 128px */,
  },
};

interface ThemeProps {
  children: ReactNode;
}

export const Theme: React.FC<ThemeProps> = ({ children }) => {
  return (
    <ThemeProvider theme={theme}>
      {children}
      <GlobalStyles />
    </ThemeProvider>
  );
};
